import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { CompetitionResultClient, CompetitionResultDTO } from '../services/apiClient';
import { API_BASE_URL, MAX_DROPDOWN_PAGE_SIZE } from '../config/config';
import axiosInstance from '../services/axiosInstance';

const apiClient = new CompetitionResultClient(API_BASE_URL, axiosInstance);

// Query hook - Get all competitionresults for dropdowns (cached 4 hours)
export const useCompetitionResults = () => {
  return useQuery({
    queryKey: ['competitionresults'],
    queryFn: async () => {
      const response = await apiClient.searchCompetitionResults({
        page: 1,
        pageSize: MAX_DROPDOWN_PAGE_SIZE,
        sortBy: 'Title',
      });
      return response?.data?.results || [];
    },
    staleTime: 4 * 60 * 60 * 1000, // 4 hours
    refetchOnReconnect: true,
  });
};

// Direct API search (no caching) - for admin tables with server-side paging
export const useCompetitionResultSearch = () => {
  const search = async (params: {
    page: number;
    pageSize: number;
    sortBy?: string;
    sortDescending?: boolean;
    filters?: any;
  }) => {
    const response = await apiClient.searchCompetitionResults({
      page: params.page,
      pageSize: params.pageSize,
      sortBy: params.sortBy || 'CreatedUtc',
      sortDescending: params.sortDescending ?? true,
      filters: params.filters || {},
    });
    return {
      results: response?.data?.results || [],
      totalCount: response?.data?.totalCount || 0,
    };
  };

  return { search };
};

// Query hook - Get single competitionResult by ID
export const useCompetitionResultById = (id: string) => {
  return useQuery({
    queryKey: ['competitionresults', id],
    queryFn: async () => {
      const response = await apiClient.getCompetitionResultById(id);
      return response?.data;
    },
    enabled: !!id,
    staleTime: Infinity,
    refetchOnReconnect: true,
  });
};

// Mutation hook - Create competitionResult
export const useCreateCompetitionResult = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CompetitionResultDTO) => {
      await apiClient.createCompetitionResult(data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['competitionresults'] });
    },
  });
};

// Mutation hook - Update competitionResult
export const useUpdateCompetitionResult = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: CompetitionResultDTO }) => {
      await apiClient.updateCompetitionResult(id, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['competitionresults'] });
    },
  });
};

// Mutation hook - Delete competitionResult
export const useDeleteCompetitionResult = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.deleteCompetitionResult(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['competitionresults'] });
    },
  });
};
